(ns admin.components.composite.crud-container
  (:require [reagent.core :as reagent]
            [re-frame.core :as re-frame]
            [reagent-form.core :as rf]
            [kyber.components.base.text :as text]
            [kyber.components.base.button :as button]
            [kyber.components.base.icons :as icon]
            [kyber.utils :as ku]
            [kyber.styles.core :refer [gs styles->classes]]
            [admin.styles.constants :refer [crud-container-max-width]]))

(def classes
  (styles->classes
   {:crud-container
    {:background-color (gs [:colors :true-white])
     :max-width (str crud-container-max-width "px")
     :margin :auto
     :padding (gs [:spacing :p16])
     :border-radius (gs [:radius :r7])
     :box-shadow (gs [:shadows :shadow-12])}

    :crud-header-container
    {:display :flex
     :justify-content :space-between
     :align-items :center
     :margin-bottom (gs [:spacing :p20])}

    :crud-header-cta-container
    {:display :flex
     :justify-content :space-between
     :align-items :center}

    :crud-container-title
    {:border-bottom (str "2px solid " (gs [:colors :primary-lite-600]))}

    :cta-container
    {:display :flex
     :justify-content :space-between
     :align-items :flex-end
     :margin-bottom (gs [:spacing :p20])}

    :search-input
    {:margin-right (gs [:spacing :p20])
     :padding-right (gs [:spacing :p20])}

    :item-row-container
    {:display :flex
     :justify-content :space-between
     :align-items :center
     :margin-bottom (gs [:spacing :p20])
     :padding-bottom (gs [:spacing :p20])
     :border-bottom (str "1px solid " (gs [:colors :gray-scale-200]))

     "&:first-child"
     {:padding-top (gs [:spacing :p20])
      :border-top (str "1px solid " (gs [:colors :gray-scale-200]))}}

    :left-cta-container
    {:display :flex
     :align-items :flex-end}}))

(defn persist-resource
  [{:keys [event-key resource-name on-success is-new]}]
  (fn [data]
    (let [success!
          (fn []
            (re-frame/dispatch
             [:notifier/create-toast
              {:message (str resource-name
                             (if (true? is-new)
                               " created."
                               " updated."))}])
            (on-success))]
      (re-frame/dispatch [event-key data success!]))))

(defn delete-resource
  [{:keys [event-key resource resource-name on-success]}]
  (fn []
    (let [success!
          (fn []
            (re-frame/dispatch
             [:notifier/create-toast
              {:message (str resource-name " removed.")}])
            (on-success))]
      (re-frame/dispatch
       [:notifier/create-alert
        {:title
         (str "Remove " resource-name "?")

         :message
         (str "Are you sure you want to remove this "
              resource-name)

         :confirm-text
         (str "Remove " resource-name)

         :deny-text
         "Cancel"

         :confirm-action
         #(re-frame/dispatch [event-key resource success!])}]))))

(defn ctas
  [{:keys [cancel-button-copy
           delete-button-copy
           is-new
           close
           short-titles
           on-delete
           resource-name
           hide-delete]
    :or {cancel-button-copy "Cancel"
         hide-delete false
         short-titles false}}]
  [:div {:class (:cta-container classes)}
   [:div {:class (:left-cta-container classes)}
    (rf/submit-button {:default-text (if (true? is-new)
                                       (cond-> "Create"
                                         (false? short-titles)
                                         (str " " resource-name))
                                       (cond-> "Save & Update"
                                         (false? short-titles)
                                         (str " " resource-name)))})

    (when (some? close)
      [button/secondary-button
       {:style {:margin-left (gs [:spacing :p20])}
        :on-click #(do
                     (.preventDefault %)
                     (close))}
       cancel-button-copy])]

   (when (and (false? hide-delete)
              (some? on-delete))
     [button/admin-button
      {:on-click #(do
                    (.preventDefault %)
                    (on-delete))}
      (or delete-button-copy
          (str "Delete " resource-name))])])

(defn- filter-collection
  [search-filter search-text collection]
  (let [search
        (partial re-find
                 (re-pattern (str "(?i)" (ku/sanitize-string search-text))))]
    (filter (fn [item]
              (let [searchable-text
                    (reduce (fn [acc search-lens]
                              (let [value (get-in item search-lens)]
                                (if (and (some? value)
                                         (string? value))
                                  (str acc value)
                                  acc)))
                            ""
                            search-filter)]
                (some? (search searchable-text))))
            collection)))

(defn- paginate
  [{:keys [per-page page]} coll]
  (let [result-count (min (* per-page page)
                          (count coll))]
    (take result-count coll)))

(defn- get-default-pagination
  [pagination]
  (cond
    (true? pagination)
    {:per-page 10 :page 1}

    (map? pagination)
    pagination))

(defn- render-show-more-button
  [pagination collection paginated-collection]
  (when (< paginated-collection collection)
    [button/primary-button
     {:on-click #(swap! pagination update :page inc)}
     "Show More"]))

(defn render
  [{:keys [pagination]
    :or {pagination true}}]
  (let [show-create-resource-form (reagent/atom false)
        editing-item (reagent/atom nil)
        is-filtering (reagent/atom false)
        filter (reagent/atom "")
        finish-filtering! (ku/debounce #(reset! is-filtering false) 320)
        update-filter! (ku/debounce #(reset! filter %) 300)
        default-pagination (get-default-pagination pagination)
        pagination* (reagent/atom default-pagination)]
    (fn [{:keys [add-resource-idx
                 title
                 sort-key
                 collection
                 context
                 edit-action
                 item-view
                 is-loading
                 resource-name
                 resource-form
                 search-filter
                 unique-key]
          :or {unique-key :uuid
               add-resource-idx false}}]
      (let [filtered-collection
            (cond->> @collection
              (true? add-resource-idx)
              (map-indexed
               #(assoc %2 :resource-idx %1))

              (and (some? @filter)
                   (some? search-filter))
              (filter-collection search-filter
                                 @filter))

            collection*
            (cond->> filtered-collection
              (some? sort-key)
              (sort-by sort-key)

              (some? pagination)
              (paginate @pagination*))

            collection-count
            (count filtered-collection)]
        [:div {:class (:crud-container classes)}
         [:div {:class (:crud-header-container classes)}

          [text/title-40 :p
           {:class (:crud-container-title classes)}
           (cond
             (true? @show-create-resource-form)
             (str "Create " resource-name)

             (some? @editing-item)
             (str "Save & Update " resource-name)

             :else
             title)]

          [:div {:class (:crud-header-cta-container classes)}
           (when (and (some? search-filter)
                      (nil? @editing-item))
             [:div {:style {:display :flex
                            :justify-content :space-between
                            :align-items :center}}
              (if @is-filtering
                [icon/icon {:class "fa-spin"
                            :style {:margin-right (gs [:spacing :p20])}}
                 :loading]
                [text/caption-20 :p
                 {:style (cond-> {:margin-right (gs [:spacing :p20])
                                  :white-space :nowrap}
                           (empty? @filter)
                           (assoc :visibility :hidden))}
                 (str collection-count
                      (if (or (> collection-count 1)
                              (zero? collection-count))
                        " results"
                        " result"))])
              [:input
               {:type :text
                :class (:search-input classes)
                :placeholder "Search"
                :on-change #(do
                              (reset! pagination* default-pagination)
                              (reset! is-filtering true)
                              (update-filter! (-> % .-target .-value))
                              (finish-filtering!))}]])

           (when (and (false? @show-create-resource-form)
                      (nil? @editing-item))
             [button/primary-button
              {:on-click #(reset! show-create-resource-form true)}
              (str "Add " resource-name)])]]

         (cond
           (true? @show-create-resource-form)
           [resource-form (cond-> {:resource-name resource-name
                                   :close #(reset! show-create-resource-form
                                                   false)}
                            (some? context)
                            (assoc :context context))]

           (some? @editing-item)
           [resource-form
            (cond-> {:resource-name resource-name
                     :close #(reset! editing-item nil)}
              (some? context)
              (assoc :context context))
            @editing-item]

           (true? @is-loading)
           [:div {:style {:display :flex
                          :align-items :center
                          :justify-content :center
                          :height "100%"}}
            [icon/icon-xl {:class "fa-spin"} :loading]]

           :else
           [:div
            (for [{key unique-key :as item} collection*]
              ^{:key key}
              [:div {:class (:item-row-container classes)}
               [item-view item]
               [button/secondary-button
                {:on-click (if (some? edit-action)
                             #(edit-action item)
                             #(reset! editing-item item))}
                (str "Edit " resource-name)]])

            [render-show-more-button
             pagination*
             (count filtered-collection)
             (count collection*)]])]))))
